<template>
  <div class="reg-fullscreen" style="background: linear-gradient(rgba(74,222,128,0.8), rgba(248,113,113,0.9), rgba(252,211,77,0.7)), url('/assets/images/fake4.png') center/cover no-repeat, #f9fafb;">
    <div class="reg-overlay">
      <div class="reg-card">
        <div class="reg-logo">
          <img src="/assets/images/CHECK-APP.png" alt="Check-App" class="logo-img" />
        </div>

        <!-- Step 1: Email + Password -->
        <template v-if="step === 1">
          <h1 class="reg-title">{{ $t('loginPortal') }}</h1>
          <form @submit.prevent="handleLogin" class="reg-form" novalidate>
            <div class="form-group">
              <label>{{ $t('email') }}</label>
              <input v-model="email" type="email" required :placeholder="$t('enterEmail')" autocomplete="username" />
            </div>
            <div class="form-group">
              <label>{{ $t('password') }}</label>
              <div class="password-wrap">
                <input v-model="password" :type="showPwd ? 'text' : 'password'" required
                       :placeholder="$t('password')" autocomplete="current-password" />
                <button type="button" @click="showPwd = !showPwd" class="pwd-toggle">
                  <i :class="showPwd ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
            </div>
            <div v-if="sessionExpired" class="alert alert-warn">Your session has expired. Please log in again.</div>
            <div v-if="error" class="alert alert-error">{{ error }}</div>
            <button type="submit" class="btn-primary" :disabled="isLoading">
              <i v-if="isLoading" class="fas fa-spinner fa-spin mr-2"></i>
              {{ isLoading ? $t('loading') : $t('signIn') }}
            </button>
          </form>
          <p class="reg-switch">
            {{ $t('noAccount') }}
            <router-link :to="`/${$i18n.locale}/register`">{{ $t('createAccount') }}</router-link>
          </p>
        </template>

        <!-- Step 2: OTP -->
        <template v-else>
          <h1 class="reg-title">{{ $t('enterOtp') }}</h1>
          <p class="otp-hint">{{ $t('otpSentMsg') }}</p>
          <form @submit.prevent="handleOtp" class="reg-form" novalidate>
            <div class="form-group">
              <label>{{ $t('enterOtp') }}</label>
              <input v-model="otp" type="text" maxlength="6" required placeholder="000000"
                     autocomplete="one-time-code" class="otp-input" />
            </div>
            <div v-if="error" class="alert alert-error">{{ error }}</div>
            <button type="submit" class="btn-primary" :disabled="isLoading">
              <i v-if="isLoading" class="fas fa-spinner fa-spin mr-2"></i>
              {{ isLoading ? $t('loading') : $t('verifyCode') }}
            </button>
            <button type="button" class="btn-secondary" @click="step = 1">
              ← {{ $t('previous') }}
            </button>
          </form>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useRegistrationStore } from '@/stores/registration';

const store    = useRegistrationStore();
const router   = useRouter();
const route    = useRoute();
const email    = ref('');
const password = ref('');
const otp      = ref('');
const showPwd  = ref(false);
const error    = ref('');
const isLoading = ref(false);
const step     = ref(1);
const sessionExpired = ref(route.query.reason === 'session_expired');

async function handleLogin() {
  error.value = '';
  isLoading.value = true;
  try {
    await store.loginStep1(email.value, password.value);
    step.value = 2;
  } catch (e) {
    error.value = e.message;
  } finally {
    isLoading.value = false;
  }
}

async function handleOtp() {
  error.value = '';
  isLoading.value = true;
  try {
    await store.loginStep2(email.value, otp.value);
    router.push(`/${route.params.lang}/register/dashboard`);
  } catch (e) {
    error.value = e.message;
  } finally {
    isLoading.value = false;
  }
}
</script>

<style scoped>
.reg-fullscreen {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.reg-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.reg-card {
  background: rgba(255,255,255,0.96);
  border-radius: 18px;
  box-shadow: 0 12px 48px rgba(0,0,0,0.18);
  padding: 44px 40px;
  width: 100%;
  max-width: 440px;
  backdrop-filter: blur(8px);
}
.reg-logo { display: flex; justify-content: center; align-items: center; margin-bottom: 20px; }
.logo-img { height: 20px; display: block; margin: 0 auto; }
.reg-title { font-size: 1.5rem; font-weight: 700; color: #1a3a5c; text-align: center; margin-bottom: 8px; }
.otp-hint { text-align: center; color: #6b7280; font-size: 0.875rem; margin-bottom: 24px; }
.reg-form { display: flex; flex-direction: column; gap: 16px; }
.form-group { display: flex; flex-direction: column; gap: 5px; }
.form-group label { font-size: .875rem; font-weight: 600; color: #374151; }
.form-group input {
  border: 1.5px solid #d1d5db; border-radius: 8px; padding: 10px 14px;
  font-size: .9rem; transition: border-color .2s; background: #fff;
}
.form-group input:focus { outline: none; border-color: #1a3a5c; }
.otp-input { letter-spacing: 0.35em; font-size: 1.4rem; text-align: center; font-weight: 700; }
.password-wrap { position: relative; }
.password-wrap input { width: 100%; padding-right: 40px; box-sizing: border-box; }
.pwd-toggle { position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: #6b7280; }
.alert-error { background: #fef2f2; border: 1px solid #fca5a5; color: #b91c1c;
  border-radius: 8px; padding: 10px 14px; font-size: .875rem; }
.alert-warn  { background: #fffbeb; border: 1px solid #fbbf24; color: #92400e;
  border-radius: 8px; padding: 10px 14px; font-size: .875rem; }
.btn-primary { background: #1a3a5c; color: #fff; border: none; border-radius: 10px;
  padding: 13px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: background .2s;
  display: flex; align-items: center; justify-content: center; }
.btn-primary:hover:not(:disabled) { background: #2d6a9f; }
.btn-primary:disabled { opacity: .6; cursor: not-allowed; }
.btn-secondary { background: transparent; color: #1a3a5c; border: 1.5px solid #1a3a5c;
  border-radius: 10px; padding: 11px; font-size: 0.9rem; font-weight: 600; cursor: pointer;
  transition: all .2s; }
.btn-secondary:hover { background: #f0f4ff; }
.reg-switch { text-align: center; margin-top: 20px; font-size: .9rem; color: #6b7280; }
.reg-switch a { color: #1a3a5c; font-weight: 600; text-decoration: none; }
</style>